"use client";

import dynamic from "next/dynamic";

import { useState } from "react";
import Image from "next/image";

import { useIsDark } from "@/context/ThemeContext";

import { EXPERTS, FEATURE_CARDS } from "@/constants/ContactData";

import Button from "@/components/common/Button";

// Dynamically import heavy, below-the-fold components
const GlowButton = dynamic(() => import("@/components/contact/GlowButton"));
const HeroSection = dynamic(() => import("@/components/common/HeroSection"));
const SectionHeader = dynamic(
  () => import("@/components/common/SectionHeader")
);
const AnimatedCarousel = dynamic(() => import("@/components/swiper"));
const ExcellenceSection = dynamic(
  () => import("@/components/contact/ExcellenceSection")
);
const BookingCards = dynamic(() => import("@/components/contact/BookingCards"));
const Connect = dynamic(() => import("@/components/contact/Connect"));
// Dynamically import heavy, below-the-fold components
const WorldMap = dynamic(() => import("@/components/contact/WorldMap"), {
  loading: () => (
    <div className="w-full h-[500px] bg-gray-200 dark:bg-gray-800 animate-pulse" />
  ),
  ssr: false,
});

const FAQSection = dynamic(() => import("@/components/contact/FAQSection"), {
  loading: () => (
    <div className="w-full h-[600px] bg-gray-200 dark:bg-gray-800 animate-pulse" />
  ),
  ssr: false,
});

const ContactForm = dynamic(() => import("@/components/contact/ContactForm"), {
  loading: () => (
    <div className="w-full h-[700px] bg-gray-200 dark:bg-gray-800 animate-pulse" />
  ),
  ssr: false,
});

const handleScroll = () => {
  const target = document.getElementById("contact-form");
  if (target) {
    target.scrollIntoView({ behavior: "smooth" });
  }
};

export default function Contact() {
  const isDark = useIsDark();

  const [order, setOrder] = useState([
    FEATURE_CARDS[0],
    FEATURE_CARDS[1],
    FEATURE_CARDS[2],
  ]);
  const flipKey = order.map((i) => i.id).join("-");

  return (
    <div className="mt-6">
      <main className="flex flex-col justify-center">
        <div className="flex flex-col items-center">
          <HeroSection
            heading="Contact"
            subHeading="Let's Build What's Next"
            paragraph="We are committed to delivering high-quality digital solutions that drive business success.  Our team offers free consultations to discuss your needs and explore solutions tailored to your business."
            headingStyles="font-alexandria font-bold text-[50px] leading-[120%] tracking-[0.02em] text-center mobile:text-[77px] sm:text-[100px] md:text-[110px] tablet:text-[130px] lg:text-[160px] xl:text-[190px] laptop:text-[230px] laptop:leading-[100%] 3xl:text-[260px] laptop:tracking-[0.09em]"
            paragraphStyles="xl:text-[30px] 3xl:text-[32px] mx-5 md:mx-[40px] lg:mx-[45] 3xl:mx-[150px]"
            scrollTarget="contact-form"
          />
        </div>
        <SectionHeader
          title="Get In touch with our experts"
          titleClassName="xl:text-[35px]"
        />

        <AnimatedCarousel
          loop={true}
          className="max-w-[2000px] mx-auto"
          breakpoints={{
            2000: { slidesPerView: 7.0, spaceBetween: 20 },
            1850: { slidesPerView: 6.5, spaceBetween: 18 },
            1700: { slidesPerView: 6.0, spaceBetween: 18 },
            1600: { slidesPerView: 5.6, spaceBetween: 16 },
            1536: { slidesPerView: 5.4, spaceBetween: 16 },
            1440: { slidesPerView: 4.9, spaceBetween: 15 },
            1280: { slidesPerView: 4.8, spaceBetween: 14 },
            1024: { slidesPerView: 4.2, spaceBetween: 12 },
            768: { slidesPerView: 3.8, spaceBetween: 10 },
            640: { slidesPerView: 3.5, spaceBetween: 8 },
            0: { slidesPerView: 3.5, spaceBetween: 8 },
          }}
        >
          {EXPERTS.map((expert, index) => (
            <div key={index} className="w-fit">
              <div className="flex flex-col font-manrope font-bold text-[22px]">
                <div
                  className="relative w-full max-h-[390px] aspect-[280/390] overflow-hidden"
                  // style={{ backgroundColor: expert.bgColor ?? "#dcdce0" }}
                >
                  {/* {expert.imgClass?.includes("mt-") && (
                    <div
                      aria-hidden
                      className={`absolute top-0 left-0 right-0 h-4 mobile:h-14 sm:h-16 md:h-18 lg:h-24 laptop:h-21 w-full pointer-events-none`}
                      style={{
                        background:
                          "linear-gradient(to bottom, #e5e5e7 0%, rgba(229,229,231,0.7) 75%, rgba(229,229,231,0.0) 100%)",
                      }}
                    />
                  )} */}
                  {/* <div
                      aria-hidden
                      className={`absolute top-0 left-0 right-0 bottom-0 w-1 pointer-events-none ${expert.bgLeft}`}
                      style={{
                        background:
                          "linear-gradient(to right, #e5e5e7 40%, rgba(229,229,231,0.7) 40%, rgba(229,229,231,0.0) 100%)",
                      }}
                    /> */}
                  <Image
                    loading="eager"
                    priority={true}
                    src={expert.icon}
                    alt={expert.name}
                    width={280}
                    height={390}
                    sizes="(max-width: 640px) 28vw, (max-width: 1024px) 24vw, (max-width: 1536px) 18vw, 14vw"
                    quality={85}
                    className={`object-full overflow-hidden ${expert.imgClass ?? ""}`}
                  />
                </div>
                <span className="dark:text-[#EEEEFF] text-[#1A1A1A]  font-bold text-[8px] mobile:text-[12px] sm:text-[13px] md:text-[15px] tablet:text-[16px] lg:text-[18px] xl:text-[20px]">
                  {expert.name}
                </span>
                <span className="text-[#676767] text-[6px] mobile:text-[10px] sm:text-[11px] md:text-[13px] tablet:text-[14px] lg:text-[15px] xl:text-[16px]">
                  {expert.title}
                </span>
              </div>
            </div>
          ))}
        </AnimatedCarousel>

        <div className="relative h-[126px] z-10 flex items-center justify-center">
          <GlowButton as="a" href="/schedule" label="Book a Consultation" />
        </div>

        <SectionHeader
          title="Book a Free Consultation"
          description="Our experts offer personalized guidance to help you make informed decisions about your digital transformation. Schedule a 30-minute consultation at your convenience"
        />

        <div>
          <ExcellenceSection
            label="EXCELLENCE"
            className="mb-10"
            isDark={isDark}
          />
        </div>

        <SectionHeader
          title="Appointment Booking Integration"
          wrapperClassName="mt-3 mb-0 md:mt-6 md:mb-3 xl:mt-10 xl:mb-6"
        />

        <div className="mt-10 flex justify-center">
          <BookingCards order={order} flipKey={flipKey} isDark={isDark} />
        </div>

        <div className="mt-12 sm:mt-14 md:mt-16 lg:mt-18 xl:mt-20 2xl:mt-26 4xl:mt-36 flex justify-center">
          <Button
            href="/schedule"
            label="Schedule Your Consultation"
            className="relative overflow-hidden md:h-9 lg:h-10 xl:h-12 laptop:h-[50px] 3xl:h-14 4xl:h-16 px-3 lg:px-6 xl:px-7 laptop:px-6 3xl:px-8 4xl:px-9
      /* Bottom glow */
      before:content-[''] before:absolute before:inset-x-0 before:-bottom-9
      before:h-[100%] before:w-[90%] before:mx-auto
      before:bg-[radial-gradient(ellipse_at_bottom,rgba(255,28,247,1)_55%,rgba(255,28,247,0.6)_75%,transparent_100%)]
      before:blur-[6px] before:rounded-t-[22px]
      before:opacity-0 before:transition-opacity before:ease-in-out
      before:duration-200 hover:before:opacity-100 hover:before:duration-800

       /* Hover outline */
      after:content-[''] after:pointer-events-none after:absolute after:inset-0
      after:rounded-[6px]
      after:border-t-1 after:border-l-1 after:border-r-1 after:border-[#ff1cf7]
      after:[mask-image:linear-gradient(to_bottom,black_0%,black_40%,transparent_70%,transparent_100%)]
      after:[-webkit-mask-image:linear-gradient(to_bottom,black_0%,black_40%,transparent_70%,transparent_100%)]
      after:opacity-0 after:transition-opacity after:duration-500
      hover:after:opacity-100
    "
            hoverOverlay={false}
            glowOnHover={false}
          />
        </div>

        <div className="mt-12 sm:mt-14 md:mt-16 lg:mt-18 xl:mt-20 flex justify-center">
          <Connect
            isDark={isDark}
            ButtonComponent={Button}
            handleScroll={handleScroll}
          />
        </div>

        <SectionHeader
          title="We work for you. We care about quality, We’re GLOBAL"
          description="So you get reliable solutions, ongoing support, and real results -- no matter where you are."
          wrapperClassName="px-2 sm:px-8 md:px-24 lg:px-40 xl:px-56 2xl:px-72 mt-20 mb-8 md:mt-28 lg:mt-32 xl:mt-40 laptop:mt-50"
        />

        <WorldMap />

        <div className="flex items-start justify-center ">
          <FAQSection isDark={isDark} />
        </div>

        <div className="flex items-start justify-center ">
          <ContactForm id="contact-form" isDark={isDark} />
        </div>
      </main>
    </div>
  );
}
